import React, { useEffect } from "react";
import { useHistory } from "react-router";
import { Button, Col, DatePicker, Divider, Form, Input, Row, Select, Spin } from "antd";
import type { FormInstance } from "antd/es/form/Form";
import moment, { Moment } from "moment";
import omit from "lodash/omit";
import { useAppSelector } from "../../..";
import type {
  DestinationEditFormProps,
  DestinationEditProps,
} from "../../../store/slices/destinations/types";
import type { ViewModeType } from "../../../lib/types";
import {
  checkIsLoading,
  getDictionaryItemById,
  geDicValueById,
  getSelectOptions,
  selectFilterOption,
  useActiveModal,
  displayFormat,
  toHttpFormat,
  infinityDateHttpFormat,
} from "../../../lib/utils";
import { areaCodesValidator, checkIsValidAreaCodesRange, onChangeAreaCodes } from "../utils";
import { AreaCodesValidateModal } from "./AreaCodesValidateModal";
import "./DestinationEdit.less";

interface ComponentProps {
  form: FormInstance<DestinationEditFormProps>;
  viewMode: Extract<ViewModeType, "create" | "edit">;
  onCancelEdit: () => void;
  getParentDestinationListByName: (name: string) => void;
  handleCleanDestinationList: () => void;
  onSubmitEditing: (params: DestinationEditProps) => void;
  isLoading: boolean;
}

type ActiveModal = "" | "validateAreaCodes" | "validateAreaCodesOnSubmit";

/* ---------------------------------------------- Начало компонента ---------------------------------------------- */

export const DestinationEdit = (props: ComponentProps) => {
  const {
    form,
    viewMode,
    onCancelEdit,
    getParentDestinationListByName,
    handleCleanDestinationList,
    onSubmitEditing,
    isLoading,
  } = props;

  const history = useHistory();

  const { activeModal, setActiveModal, activeModalOptions } = useActiveModal<ActiveModal>("");

  const { countries, regions, destinationCategories, destinationTypes } = useAppSelector(
    (state) => state.dicts
  );

  const {
    destination,
    destinationSets,
    parentDestinationList,
    parentDestinationListLoading,
  } = useAppSelector((state) => state.destinations);

  const getRegionId = (countryId: number | undefined): number | undefined =>
    getDictionaryItemById(countries, countryId)?.parentDicId || undefined;

  const getFullName = (props: { name?: string; parentDestinationId?: number }): string => {
    const { name, parentDestinationId } = props;
    const parentDestinationName =
      parentDestinationId && geDicValueById(parentDestinationList, parentDestinationId)?.name;
    return [parentDestinationName, name].filter(Boolean).join(" - ");
  };

  const getFullShortName = (props: {
    shortName?: string;
    parentDestinationId?: number;
  }): string => {
    const { shortName, parentDestinationId } = props;
    const parentDestShortName =
      parentDestinationId && geDicValueById(parentDestinationList, parentDestinationId)?.shortName;
    return [parentDestShortName, shortName].filter(Boolean).join(" - ");
  };

  const setInitialValues = () => {
    if (viewMode === "create") {
      form.setFieldsValue({ fromDate: moment() });
    }
    if (destination) {
      const {
        areaCodes,
        countryId,
        name,
        shortName,
        parentDestinationId,
        fromDate,
        toDate,
      } = destination;
      const areaCodesString = areaCodes.join(";");
      const initialValues = {
        ...destination,
        areaCodes: areaCodesString,
        prevAreaCodes: areaCodesString,
        regionId: getRegionId(countryId),
        fullName: getFullName({ name, parentDestinationId }),
        fullShortName: getFullShortName({ shortName, parentDestinationId }),
        fromDate: fromDate ? moment(fromDate) : undefined,
        toDate: toDate ? moment(toDate) : undefined,
      };
      form.setFieldsValue(initialValues);
    }
  };

  // Установка через эффект, а не напрямую в форму, т.к. не успевает срабатывать корректно
  useEffect(() => {
    setInitialValues();
  }, [viewMode]);

  const setFullName = () => {
    const values = form.getFieldsValue(["name", "parentDestinationId"]);
    const fullName = getFullName(values);
    form.setFieldsValue({ fullName });
  };

  const setFullShortName = () => {
    const values = form.getFieldsValue(["shortName", "parentDestinationId"]);
    const fullShortName = getFullShortName(values);
    form.setFieldsValue({ fullShortName });
  };

  const setDestinationTypeId = () => {
    const { destinationSetId } = form.getFieldsValue();
    const destinationTypeId =
      destinationSetId && geDicValueById(destinationSets, destinationSetId)?.destinationTypeId;
    form.setFieldsValue({ destinationTypeId });
  };

  const onChangeFromDate = (fromDate: Moment | null) => {
    const toDate = form.getFieldValue("toDate");
    if (fromDate && fromDate.isAfter(toDate)) {
      form.resetFields(["toDate"]);
    }
  };

  const disabledToDate = (data: Moment) => {
    const fromDate = form.getFieldValue("fromDate");
    if (fromDate) {
      return data.isBefore(fromDate);
    }
    return false;
  };

  const onFinish = (values: DestinationEditFormProps) => {
    const isValidRange = checkIsValidAreaCodesRange(values.areaCodes);
    console.log(values.areaCodes.split(";"));
    if (isValidRange) {
      const creatingValues: DestinationEditProps = {
        ...omit(values, ["regionId", "fullName", "fullShortName", "prevAreaCodes"]),
        areaCodes: values.areaCodes.split(";"),
        fromDate: toHttpFormat(values.fromDate),
        toDate: values.toDate ? toHttpFormat(values.toDate) : infinityDateHttpFormat,
      };
      onSubmitEditing(creatingValues);
    } else {
      setActiveModal("validateAreaCodesOnSubmit", { areaCodes: values.areaCodes });
      form.setFields([
        {
          name: "areaCodes",
          errors: [""],
        },
      ]);
    }
  };

  const disableFromDate = (date) => {
    if (viewMode === "create") {
      return false;
    }
    return date.isBefore(destination?.fromDate);
  };

  return (
    <div className="destination-edit">
      <Spin spinning={isLoading}>
        <Form
          style={{ marginTop: 20 }}
          form={form}
          onFinish={onFinish}
          wrapperCol={{ span: 14 }}
          labelCol={{ span: 7 }}
          labelAlign="left"
        >
          <Row gutter={30}>
            <Col span={11}>
              <Form.Item
                label="Набор"
                name="destinationSetId"
                rules={[
                  {
                    required: viewMode === "create",
                  },
                ]}
                className="form-item-required__hidden-error"
              >
                <Select
                  allowClear
                  disabled={viewMode === "edit"}
                  showSearch
                  options={getSelectOptions({
                    list: destinationSets,
                  })}
                  filterOption={selectFilterOption}
                  onChange={setDestinationTypeId}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Страна"
                name="countryId"
                rules={[
                  {
                    required: true,
                  },
                ]}
                className="form-item-required__hidden-error"
              >
                <Select
                  allowClear
                  showSearch
                  options={getSelectOptions({
                    list: countries,
                  })}
                  filterOption={selectFilterOption}
                  onChange={(countryId: number) => {
                    form.setFieldsValue({ regionId: getRegionId(countryId) });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={30}>
            <Col span={11}>
              <Form.Item
                label="Наименование"
                name="name"
                rules={[
                  {
                    required: true,
                  },
                ]}
                className="form-item-required__hidden-error"
              >
                <Input allowClear onChange={setFullName} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Регион" name="regionId">
                <Select
                  disabled
                  showArrow={false}
                  options={getSelectOptions({
                    list: regions,
                  })}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={30}>
            <Col span={11}>
              <Form.Item
                label="Аббревиатура"
                name="shortName"
                rules={[
                  {
                    required: true,
                  },
                ]}
                className="form-item-required__hidden-error"
              >
                <Input allowClear onChange={setFullShortName} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Родительское направление" name="parentDestinationId">
                <Select
                  showSearch
                  allowClear
                  notFoundContent={
                    checkIsLoading(parentDestinationListLoading) && <Spin size="small" />
                  }
                  filterOption={selectFilterOption}
                  onSearch={getParentDestinationListByName}
                  onClear={handleCleanDestinationList}
                  options={parentDestinationList.map((option) => ({
                    option,
                    label: [option?.name, option?.shortName].filter(Boolean).join(" - "),
                    value: option.id,
                  }))}
                  onChange={() => {
                    setFullName();
                    setFullShortName();
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={30}>
            <Col span={11}>
              <Form.Item label="Полное наименование" name="fullName">
                <Input disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Категория"
                name="categoryId"
                rules={[
                  {
                    required: true,
                  },
                ]}
                className="form-item-required__hidden-error"
              >
                <Select
                  showSearch
                  allowClear
                  filterOption={selectFilterOption}
                  options={getSelectOptions({
                    list: destinationCategories,
                  })}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={30}>
            <Col span={11}>
              <Form.Item label="Полная аббревиатура" name="fullShortName">
                <Input disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Тип" name="destinationTypeId">
                <Select
                  disabled
                  showArrow={false}
                  options={getSelectOptions({
                    list: destinationTypes,
                  })}
                />
              </Form.Item>
            </Col>
          </Row>
          <Divider style={{ margin: "0 0 8px" }} />
          <h3>Коды в направлении</h3>
          <Divider style={{ margin: "0 0 20.5px" }} />
          <Row>
            <Col span={19}>
              <div className="destination-edit-areaCodes__input">
                <label htmlFor="areaCodes">Коды</label>
                <Form.Item
                  name="areaCodes"
                  wrapperCol={{ span: 24 }}
                  rules={[
                    {
                      validator: areaCodesValidator,
                    },
                  ]}
                  className="form-item-required__hidden-error"
                >
                  <Input.TextArea
                    id="areaCodes"
                    rows={5}
                    allowClear
                    onChange={(e) => {
                      const { value } = e.target;
                      onChangeAreaCodes({
                        value,
                        form,
                        openValidateModal: () => {
                          setActiveModal("validateAreaCodes", { areaCodes: value });
                        },
                      });
                    }}
                  />
                </Form.Item>
              </div>
            </Col>
            <Col offset={1} span={4} className="destination-edit-period">
              <div style={{ marginBottom: "9px" }}>
                <label htmlFor="fromDate">Период действия</label>
              </div>
              <Form.Item
                labelCol={{ span: 4 }}
                name="fromDate"
                label={<>С</>}
                rules={[
                  {
                    required: true,
                  },
                ]}
                className="form-item-required__hidden-error"
              >
                <DatePicker
                  format={displayFormat}
                  id="fromDate"
                  onChange={onChangeFromDate}
                  disabledDate={disableFromDate}
                />
              </Form.Item>
              <Form.Item
                name="toDate"
                label="До"
                labelCol={{ span: 4 }}
                dependencies={["fromDate"]}
              >
                <DatePicker format={displayFormat} disabledDate={disabledToDate} />
              </Form.Item>
            </Col>
          </Row>
          <Divider style={{ marginTop: 0 }} />
          <Row justify="center" gutter={20} style={{ paddingBottom: "20px" }}>
            <Col>
              <Button onClick={onCancelEdit}>Отменить</Button>
            </Col>
            <Col>
              <Button type="primary" htmlType="submit">
                Сохранить
              </Button>
            </Col>
          </Row>
        </Form>
      </Spin>
      <AreaCodesValidateModal
        visible={activeModal === "validateAreaCodes"}
        validatingAreaCodes={activeModalOptions?.areaCodes || ""}
        closeModal={() => setActiveModal("")}
        saveAreaCodes={(areaCodes: string) =>
          form.setFieldsValue({ areaCodes, prevAreaCodes: areaCodes })
        }
      />
      <AreaCodesValidateModal
        onSubmitValidation
        visible={activeModal === "validateAreaCodesOnSubmit"}
        validatingAreaCodes={activeModalOptions?.areaCodes || ""}
        closeModal={() => setActiveModal("")}
        saveAreaCodes={(areaCodes: string) =>
          form.setFieldsValue({ areaCodes, prevAreaCodes: areaCodes })
        }
      />
    </div>
  );
};
